/// <reference lib="dom" />
import type {
  VouchConfig,
  ApiResponse,
  ApiListResponse,
  Program,
  ProgramStats,
  PaymentIntent,
  Voucher,
  VoucherBalanceStatus,
  UPIData,
  UPIIntentResponse,
  AuthorizeResponse,
  IntentStatusResponse,
} from "./types";
import { resolveBaseUrl } from "./constants";

export class Vouch {
  private apiKey: string;
  private baseUrl: string;
  private version: string;
  readonly showVoucherOnlyWithBalance: boolean;

  constructor(config: VouchConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = resolveBaseUrl(config.baseUrl);
    this.version = config.version ?? "2026-04-06";
    this.showVoucherOnlyWithBalance = config.showVoucherOnlyWithBalance ?? false;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": this.apiKey,
        "Vouch-Version": this.version,
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    const json = await res.json();

    if (!res.ok) {
      const error = json.error ?? json;
      const message =
        typeof error === "string"
          ? error
          : (error.message ?? json.message ?? "Request failed");
      const code =
        typeof error === "string"
          ? "API_ERROR"
          : (error.code ?? "API_ERROR");
      const requestId = typeof error === "object" && error ? error.request_id : undefined;
      throw new VouchError(code, message, res.status, requestId);
    }

    return json as T;
  }

  programs = {
    list: () => this.request<{ programs: Program[] }>("GET", "/programs"),

    get: (slug: string) => this.request<{ program: Program }>("GET", `/programs/${slug}`),

    create: (params: {
      slug: string;
      name: string;
      currency?: string;
      currencySymbol?: string;
      description?: string;
      defaultLocale?: string;
      supportedLocales?: string[];
    }) => this.request<ApiResponse<{ program: Program }>>("POST", "/programs", params),

    stats: (slug: string) => this.request<ApiResponse<{ stats: ProgramStats }>>("GET", `/programs/${slug}/stats`),

    enrol: (slug: string, params: {
      phone: string;
      fields?: Record<string, unknown>;
    }, options?: { idempotencyKey?: string }) =>
      this.request<ApiResponse<unknown>>("POST", `/programs/${slug}/enrol`, params),
  };

  payments = {
    createIntent: (params: {
      merchantId: string;
      items: Array<{ sku: string; name: string; categoryCode: string; qty: number; unitPrice: number }>;
      programId?: string;
    }) => this.request<ApiResponse<PaymentIntent>>("POST", "/payments/intents", params),

    quote: (params: { intentId: string; tokenId: number }) =>
      this.request<ApiResponse<unknown>>("POST", "/payments/quote", params),

    authorize: (params: { intentId: string }) =>
      this.request<AuthorizeResponse>("POST", "/payments/authorize", params),

    /**
     * Initiate a payment from a scanned UPI QR code.
     *
     * Resolves the merchant by UPI ID, looks up the beneficiary's voucher
     * (via tokenId + programId, or via the authenticated session), creates
     * a PaymentIntent, and auto-quotes if a matching voucher is found.
     *
     * For partner integrations using API key auth, pass `beneficiaryPhone`
     * or `beneficiaryId` to identify the beneficiary without a session.
     *
     * Set `externalSettlement: true` when your platform moves the money itself
     * (open-network UPI). Settlement then records the voucher ledger entry and
     * marks the payment SETTLED, but Vouch does NOT call the UPI rail engine to
     * perform/confirm the payout. Optionally pass your own `payoutReference`.
     */
    initiateUPI: (params: {
      upiData: UPIData;
      tokenId?: number;
      programId: string;
      beneficiaryPhone?: string;
      beneficiaryId?: string;
      externalSettlement?: boolean;
      payoutReference?: string;
    }) =>
      this.request<UPIIntentResponse>("POST", "/payments/upi-intent", {
        upiId: params.upiData.upiId,
        merchantName: params.upiData.merchantName,
        amount: params.upiData.amount,
        mcc: params.upiData.mcc,
        raw: params.upiData.raw,
        tokenId: params.tokenId,
        programId: params.programId,
        beneficiaryPhone: params.beneficiaryPhone,
        beneficiaryId: params.beneficiaryId,
        externalSettlement: params.externalSettlement,
        payoutReference: params.payoutReference,
      }),

    getIntent: (intentId: string) =>
      this.request<IntentStatusResponse>("GET", `/payments/${intentId}`),
  };

  vouchers = {
    verify: (token: string, programId?: string) =>
      this.request<ApiResponse<Voucher>>("POST", "/verify", { token, programId }),

    getBalance: (params: { beneficiaryPhone: string; programId: string }) => {
      const q = new URLSearchParams({
        phone: params.beneficiaryPhone,
        programId: params.programId,
      });
      return this.request<{ success: true } & VoucherBalanceStatus>(
        "GET",
        `/beneficiaries/voucher-balance?${q.toString()}`,
      );
    },

    shouldShowUI: async (params: { beneficiaryPhone: string; programId: string }): Promise<boolean> => {
      if (!this.showVoucherOnlyWithBalance) return true;
      const balance = await this.vouchers.getBalance(params);
      return balance.hasBalance;
    },
  };

  merchants = {
    list: (programId?: string) =>
      this.request<ApiListResponse<unknown>>("GET", programId ? `/merchants?programId=${programId}` : "/merchants"),
  };
}

export class VouchError extends Error {
  code: string;
  statusCode: number;
  requestId?: string;

  constructor(code: string, message: string, statusCode: number, requestId?: string) {
    super(message);
    this.name = "VouchError";
    this.code = code;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}
